import { Tabs } from 'antd';
import { locale } from 'locales/i18nManager';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { DailyStatus } from 'services/api/models/SymptomTrackingDTO';
import { colors, palette } from 'design';

export type CalendarTabsProps = {
  daysWithNotes: DailyStatus[];
};

const useStyles = createUseStyles({
  dailyCommentTabs: {
    width: '100%',
    overflowX: 'scroll',

    '& .ant-tabs-tab': {
      backgroundColor: colors.surface.page,
      width: 50,
      marginRight: '8px',
      justifyContent: 'center',
      textAlign: 'center',
      border: 'none',
      borderRadius: '10px',
    },

    '& .ant-tabs-tab-active': {
      backgroundColor: palette.derived.tealMid,
      '& .ant-tabs-tab-btn': {
        color: 'white',
      },
    },

    '& .ant-tabs-nav::before': {
      border: 'none',
    },
    '& .ant-tabs-content-holder': {
      backgroundColor: colors.surface.page,
      borderRadius: 10,
      padding: '0px 16px',
    },
  },
  dayTabHeader: {
    margin: '2px 0px',
    textTransform: 'capitalize',
    fontWeight: 'bold',
    lineHeight: 1.2,
  },
  dayLabel: {
    color: colors.text.disabled,
  },
  longText: {
    whiteSpace: 'pre-line',
  },
  noDataText: {
    color: colors.text.disabled,
    '& p': {
      margin: 0,
    },
  },
});

const CalendarTabs: React.FC<CalendarTabsProps> = ({ daysWithNotes }) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const lastItemKey = daysWithNotes[daysWithNotes.length - 1]?.date || 'nokey';
  const tabItems = daysWithNotes.map(status => {
    const statusDate = new Date(status.date as string);
    return {
      key: status.date as string,
      label: (
        <p className={styles.dayTabHeader}>
          <span className={styles.dayLabel}>
            {statusDate.toLocaleDateString(locale, { weekday: 'long' }).slice(0, 2)}
          </span>
          <br />
          {statusDate.toLocaleDateString(locale, { day: 'numeric' })}
        </p>
      ),
      children: (
        <>
          {status.comment && (
            <>
              <h3>
                {t('symptomTracking.graphs.notes')} -{' '}
                {statusDate.toLocaleDateString(locale, { month: 'short', day: 'numeric' })}
              </h3>
              <p className={styles.longText}>{status.comment}</p>
            </>
          )}
          {status.extra_symptoms && (
            <>
              <h3>
                {t('symptomTracking.graphs.otherSymptoms')}
                {!status.comment && (
                  <>
                    {' - '}
                    {statusDate.toLocaleDateString(locale, { month: 'short', day: 'numeric' })}
                  </>
                )}
              </h3>
              <p className={styles.longText}>{status.extra_symptoms}</p>
            </>
          )}
        </>
      ),
    };
  });
  return daysWithNotes.length ? (
    <Tabs type="card" className={styles.dailyCommentTabs} defaultActiveKey={lastItemKey} items={tabItems} />
  ) : (
    <div className={styles.noDataText}>
      <p>{t('symptomTracking.notes.noNotesYet')}</p>
    </div>
  );
};
export default CalendarTabs;
